import { PricingResponse } from "../typings/interfaces/responses/pricing-response";

/**
 * Which menu combinations have a real (> $0) price in the loaded price list,
 * so the POS can hide the rest. Services in `zeroPriceServices` also offer
 * their $0 rows (e.g. Shoe & Luggage shoe types, priced by their repairs).
 * Keys are ids joined with ":".
 */
export interface PricedMenuIndex {
  combos: Set<string>; // service:garment:style:fabric
  styles: Set<string>; // service:garment:style (with any fabric)
  garments: Set<string>; // service:garment (any style / fabric)
  garmentFabrics: Set<string>; // service:garment:fabric (any style)
}

export const buildPricedMenuIndex = (
  rows: Array<PricingResponse> | null | undefined,
  zeroPriceServices: ReadonlySet<number> = new Set()
): PricedMenuIndex | null => {
  if (!rows) return null;
  const index: PricedMenuIndex = {
    combos: new Set(),
    styles: new Set(),
    garments: new Set(),
    garmentFabrics: new Set(),
  };
  for (const r of rows) {
    if (!(r.price > 0) && !(r.price === 0 && zeroPriceServices.has(r.categoryId))) continue;
    index.combos.add(`${r.categoryId}:${r.subCategoryId}:${r.secondSubCategoryId}:${r.materialId}`);
    index.styles.add(`${r.categoryId}:${r.subCategoryId}:${r.secondSubCategoryId}`);
    index.garments.add(`${r.categoryId}:${r.subCategoryId}`);
    index.garmentFabrics.add(`${r.categoryId}:${r.subCategoryId}:${r.materialId}`);
  }
  return index;
};

/** A style is offered when it has a price - with the chosen fabric, if any. */
export const isStylePriced = (
  index: PricedMenuIndex | null,
  service: number | undefined,
  garment: number,
  style: number,
  fabric?: number
) =>
  !index ||
  (fabric
    ? index.combos.has(`${service}:${garment}:${style}:${fabric}`)
    : index.styles.has(`${service}:${garment}:${style}`));

/** A fabric is offered when the chosen garment (and style, if any) has a price with it. */
export const isFabricPriced = (
  index: PricedMenuIndex | null,
  service: number | undefined,
  fabric: number,
  garment?: number,
  style?: number
) =>
  !index ||
  !garment ||
  (style
    ? index.combos.has(`${service}:${garment}:${style}:${fabric}`)
    : index.garmentFabrics.has(`${service}:${garment}:${fabric}`));

export const isGarmentPriced = (
  index: PricedMenuIndex | null,
  service: number | undefined,
  garment: number
) => !index || index.garments.has(`${service}:${garment}`);
